/**
 * @module cercaliagl/service/Distance
 */
import turfArea from '@turf/area';
import turfLength from '@turf/length';
import Service from './Service';
import { KEY } from '../globals';
import LonLat from '../LonLat';
import { assert } from '../AssertionError';
import { i18n } from '../dictionary';
import { distanceBetweenPoints } from '../util';

/**
 * @typedef {Object} DistanceOptions
 * @property {boolean} [calculateGeometry] Specify if you need to obtain the shape geometry when calculating the distance. For weight=='line' this option is not avaliable.
 * @property {LonLat} [origin] Origin. If not defined, then use `setOrigin` to initialize variable;
 * @property {LonLat} [destination] Destination. If not defined, then use `setDestination` to initialize variable;
 * @property {Array.<LonLat>} [steps] Sequential stop list.
 * @property {string|undefined} [weight] Distance calculation considering weight/type `line|time|distance|money`. Default value `line`.
 */

/**
 * @classdesc
 *
 * Service class to calculate distance between two or more points.<br/>
 * It can calculate distance by `weight`. Weight available are: `
 * @api
 */
class Distance extends Service {
  /**
   * @param {DistanceOptions} opt_options Params and options for distance calculation
   */
  constructor(options = {}) {
    super();
    /**
     * @type {LonLat|null}
     * @private
     */
    this.origin_ = options.origin ? options.origin : null;

    /**
     * @type {LonLat|null}
     * @private
     */
    this.destination_ = options.destination ? options.destination : null;

    /**
     * @type {Array.<LonLat>}
     * @private
     */
    this.steps_ = options.steps ? options.steps : [];

    /**
     * @type {string}
     * @private
     */
    this.weight_ = options.weight ? options.weight : 'line';

    /**
     * @type {boolean}
     * @private
     */
    this.calculateGeometry_ = !!options.calculateGeometry;

    /**
     * @type {number|null}
     * @private
     */
    this.distance_ = null;
  }

  /**
   * Set origin point.
   * @param {LonLat} lonLat Origin point
   * @api
   */
  setOrigin(lonLat) {
    assert(lonLat instanceof LonLat, i18n('asserInvalidObjectLonLat'));
    this.origin_ = lonLat;
  }

  /**
   * Obtain origin.
   * @return {LonLat|null} Returns the origin assigned previously
   * @api
   */
  getOrigin() {
    return this.origin_;
  }

  /**
   * Set destination point.
   * @param {LonLat} lonLat Destination point
   * @api
   */
  setDestination(lonLat) {
    assert(lonLat instanceof LonLat, i18n('asserInvalidObjectLonLat'));
    this.destination_ = lonLat;
  }

  /**
   * Returns the destination.
   * @return {LonLat|null} Destination point
   * @api
   */
  getDestination() {
    return this.destination_;
  }

  /**
   * Set steps (or waypoints)
   * @param {Array.<LonLat>} steps Array with steps
   * @api
   */
  setSteps(steps) {
    this.steps_ = steps;
  }

  /**
   * Get steps (or waypoints)
   * @return {Array.<LonLat>} Steps
   * @api
   */
  getSteps() {
    return this.steps_;
  }

  /**
   * Set weight to calculate distance
   * @param {string} weight Weight. Available values `line|time|distance|money`
   * @api
   */
  setWeight(weight) {
    this.weight_ = weight || 'line';
  }

  /**
   * Get weight. Available values `line|time|distance|money`
   * @return {string} weight
   * @api
   */
  getWeight() {
    return this.weight_;
  }

  /**
   * @param {boolean} calculateGeometry `true` if geometry will be returned after calculation..
   * @api
   */
  setCalculateGeometry(calculateGeometry) {
    this.calculateGeometry_ = !!calculateGeometry;
  }

  /**
   * Returns if calculate geometry functionality is active or inactive
   * @return {boolean} calculateGeometry `true` or `false`. If calculate geometry option is enabled.
   * @api
   */
  isCalculateGeometry() {
    return this.calculateGeometry_;
  }

  /**
   * Distance calculation with the assigned parameters, and returns the data to the callback function
   * which is called once server returns response.
   * @param {Function} callbackFn Callback function
   * @api
   */
  calculate(callbackFn) {
    if (this.weight_ === 'line') {
      const obj = {
        cercalia: {
          distance: null,
          weight: this.weight_,
          stages: {
            stage: [],
          },
          stoplist: {
            stop: [],
          },
        },
      };

      const arrCoordinatesSorted = [];

      arrCoordinatesSorted.push([this.origin_.getLon(), this.origin_.getLat()]);
      obj.cercalia.stoplist.stop.push({
        id: 0,
        coord: {
          x: this.origin_.getLon(),
          y: this.origin_.getLat(),
        },
      });

      this.steps_.forEach((elem, i) => {
        arrCoordinatesSorted.push([elem.getLon(), elem.getLat()]);
        obj.cercalia.stoplist.stop.push({
          id: i + 1,
          coord: {
            x: elem.getLon(),
            y: elem.getLat(),
          },
        });
      });

      arrCoordinatesSorted.push([this.destination_.getLon(), this.destination_.getLat()]);
      obj.cercalia.stoplist.stop.push({
        id: this.steps_.length + 1,
        coord: {
          x: this.destination_.getLon(),
          y: this.destination_.getLat(),
        },
      });

      // Calculamos distancia etapa a etapa
      let total = 0;

      for (let i = 0; i < arrCoordinatesSorted.length - 1; i += 1) {
        let dist = distanceBetweenPoints(arrCoordinatesSorted[i], arrCoordinatesSorted[i + 1]);
        dist /= 1000; // To Km.
        total += dist;
        obj.cercalia.stages.stage.push({
          id: i,
          dist,
        });
      }

      obj.cercalia.distance = total;

      callbackFn(obj);
    } else if (['time', 'distance', 'money'].indexOf(this.weight_) !== -1) {
      const moO = `${this.origin_.getLat()},${this.origin_.getLon()}`;
      const moD = `${this.destination_.getLat()},${this.destination_.getLon()}`;
      const params = {
        key: KEY,
        weight: this.weight_,
        cmd: 'distance',
        mo_o: moO,
        mo_d: moD,
        calculateGeometry: this.calculateGeometry_ ? 1 : undefined,
      };

      this.steps_.forEach((elem, i) => {
        params[`mo_${i + 1}`] = `${elem.getLat()},${elem.getLon()}`;
      });

      this.getRequest({
        data: params,
        callbackFn: (data) => {
          try {
            data.cercalia.weight = this.weight_; // eslint-disable-line no-param-reassign
            callbackFn(data);
          } catch (e) {
            console.error(e); // eslint-disable-line no-console
          }
        },
      });
    } else {
      assert(false, i18n('assertUknownWeight') + this.weight_);
    }
  }

  /**
 * @api
 * @param {GeoJSON} line LineString || MultiLineString
 * @param {string} units Kilometers || miles || radians || degrees. Kilometers by default
 * @return {number} Distance in the specified units
 */
  static calculateDistanceOnLine(line, units) {
    const unitOptions = {
      units: units || 'kilometers',
    };
    return turfLength(line, unitOptions);
  }

  /**
 * @api
 * @param {GeoJSON} area
 * @return {number} Distance in quare meters
 */
  static calculateDistanceOnArea(area) {
    return turfArea(area);
  }

  setDataFromGeometry(geometry) {
    const { coordinates } = geometry;
    const firstCoordinate = coordinates[0];
    const lastCoordinate = coordinates[coordinates.length - 1];
    if (coordinates.length > 2) {
      this.steps_ = [];
      const finalIndex = coordinates.length - 1;
      for (let i = 1; i < finalIndex; i += 1) {
        this.steps_.push(new LonLat(coordinates[i][0], coordinates[i][1]));
      }
    }
    this.origin_ = new LonLat(firstCoordinate[0], firstCoordinate[1]);
    this.destination_ = new LonLat(lastCoordinate[0], lastCoordinate[1]);
  }
}

export default Distance;
